import apiService from './api';
import type {
  Job,
  JobFormData,
  JobsListResponse,
  WaypointInput,
  LocationRecord,
  CoverageResult,
  JobLiveStatus,
  JobStats,
} from '../types/jobs';

// ============================================================================
// JOB SERVICE
// ============================================================================

// apiService.get/post/put already unwrap response.data,
// so they return { success: true, data: ... } directly.

/**
 * The farm_jobs seats that reach past the reader's own jobs (`canOperateJob`
 * on the server). Without one, anybody in the org can still pre-start, run
 * and finish a job they are on — using a machine safely is not a paid seat —
 * and the server narrows every list to their own.
 */
export const JOB_OPERATE_SEATS = ['operator', 'supervisor', 'creator'];

/** Stands in for the list when it could not be read: nothing assigned. */
export const NO_JOBS: { success: boolean; data: JobsListResponse } =
  { success: true, data: { jobs: [], total: 0, page: 1, limit: 0 } };

class JobService {

  // ==========================================================================
  // JOBS CRUD
  // ==========================================================================

  async getJobs(filters?: {
    status?: string;
    farm_id?: number;
    assigned_to?: number;
    equipment_id?: number;
    activity_type_id?: number;
    page?: number;
    limit?: number;
  }): Promise<{ success: boolean; data: JobsListResponse }> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.farm_id) params.append('farm_id', String(filters.farm_id));
    if (filters?.assigned_to) params.append('assigned_to', String(filters.assigned_to));
    if (filters?.equipment_id) params.append('equipment_id', String(filters.equipment_id));
    if (filters?.activity_type_id) params.append('activity_type_id', String(filters.activity_type_id));
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));

    const qs = params.toString();
    return apiService.get(`/jobs${qs ? `?${qs}` : ''}`) as any;
  }

  async getJob(id: number): Promise<{ success: boolean; data: Job }> {
    return apiService.get(`/jobs/${id}`) as any;
  }

  async createJob(formData: JobFormData): Promise<{ success: boolean; data: Job }> {
    return apiService.post('/jobs', formData) as any;
  }

  /**
   * Create a minimal auto-assigned job for ad-hoc equipment use. Only requires
   * the operator license (vs. full createJob which requires creator).
   */
  async createQuickJob(data: {
    equipment: { primary_id: number; implement_ids?: number[] };
    name?: string;
    farm_id?: number | null;
    /** An assessor who is not inducted signing themselves off to start. */
    induction_override?: boolean;
  }): Promise<{ success: boolean; data: Job }> {
    return apiService.post('/jobs/quick', data) as any;
  }

  async updateJob(id: number, formData: Partial<JobFormData>): Promise<{ success: boolean; data: Job }> {
    return apiService.put(`/jobs/${id}`, formData) as any;
  }

  async assignUsers(
    jobId: number,
    userIds: number[],
    induction?: { induction_mode?: 'induct' | 'request'; induction_note?: string },
  ): Promise<{ success: boolean; data: Job }> {
    return apiService.post(`/jobs/${jobId}/assign`, { user_ids: userIds, ...induction }) as any;
  }

  // ==========================================================================
  // STATUS TRANSITIONS
  // ==========================================================================

  /**
   * `checklists` carries one pre-start completion per machine on the job —
   * the prime mover and each implement — because a checklist run against the
   * tractor's equipment type says nothing about the implement behind it.
   *
   * The server still accepts the older single `checklist` (implicitly the
   * prime mover) from OTA bundles already on devices, and prefers
   * `checklists` when both arrive. Nothing new should send the singular form.
   */
  async startJob(
    jobId: number,
    startData?: {
      hours_start?: number;
      km_start?: number;
      checklists?: {
        equipment_id: number;
        template_id: number;
        items: { template_item_id: number; passed: boolean; notes?: string }[];
        maintenance_items?: { interval_id: number; passed: boolean }[];
      }[];
      /** An assessor who is not inducted signing themselves off to start. */
      induction_override?: boolean;
    },
  ): Promise<{ success: boolean; data: Job }> {
    return apiService.post(`/jobs/${jobId}/start`, startData || {}) as any;
  }

  async pauseJob(jobId: number): Promise<{ success: boolean; data: Job }> {
    return apiService.post(`/jobs/${jobId}/pause`) as any;
  }

  async resumeJob(jobId: number): Promise<{ success: boolean; data: Job }> {
    return apiService.post(`/jobs/${jobId}/resume`) as any;
  }

  async completeJob(jobId: number, endData?: { hours_end?: number; km_end?: number }): Promise<{ success: boolean; data: Job }> {
    return apiService.post(`/jobs/${jobId}/complete`, endData || {}) as any;
  }

  async cancelJob(jobId: number): Promise<{ success: boolean; data: Job }> {
    return apiService.post(`/jobs/${jobId}/cancel`) as any;
  }

  // ==========================================================================
  // GPS WAYPOINTS
  // ==========================================================================

  async postWaypoints(jobId: number, waypoints: WaypointInput[]): Promise<{ success: boolean; data: { inserted: number } }> {
    return apiService.post(`/jobs/${jobId}/waypoints`, { waypoints }) as any;
  }

  async getWaypoints(jobId: number, userId?: number): Promise<{ success: boolean; data: LocationRecord[] }> {
    const qs = userId ? `?user_id=${userId}` : '';
    return apiService.get(`/jobs/${jobId}/waypoints${qs}`) as any;
  }

  // ==========================================================================
  // COVERAGE & LIVE STATUS
  // ==========================================================================

  async getCoverage(jobId: number): Promise<{ success: boolean; data: CoverageResult }> {
    return apiService.get(`/jobs/${jobId}/coverage`) as any;
  }

  async getJobStats(jobId: number): Promise<{ success: boolean; data: JobStats }> {
    return apiService.get(`/jobs/${jobId}/stats`) as any;
  }

  async getLiveStatus(jobId: number): Promise<{ success: boolean; data: JobLiveStatus }> {
    return apiService.get(`/jobs/${jobId}/live`) as any;
  }

  // ==========================================================================
  // AUTOCOMPLETE
  // ==========================================================================

  async getAutocomplete(): Promise<{ success: boolean; data: string[] }> {
    return apiService.get('/jobs/autocomplete') as any;
  }
}

const jobService = new JobService();
export default jobService;
